import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Permissioned from 'dashboard/components/Permissioned';
import Pii from 'dashboard/components/Pii';
import { MonoSpan } from 'dashboard/components/Typography';
import WebhookEventStatusSticker from 'dashboard/components/WebhookEventStatusSticker';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiEndpoint, ApiModel, ApiResource } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const StyledModalContainer = styled(ModalContainer)`
  width: 720px;
`;

const ModalHeaderChildren = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: ${SPACINGS.base * 2}px;
  gap: ${SPACINGS.base}px;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: 70vh;
`;

const StyledMonoSpan = styled(MonoSpan)`
  font-size: ${FONT_SIZES.small};
`;

const CodeContainer = styled('div')`
  margin: ${SPACINGS.base * 2}px 0;
`;

const TAB_WIDTH = 2;

interface WebhookEventModalProps {
  webhookEventId: string;
  // Whether this is rendered from the perspective of a webhook or an event
  context: 'webhook' | 'event';
  handleClose: () => void;
  handleWebhookEventUpdate?: (webhookEvent: WebhookEvent) => void;
}

/**
 * Webhook events detail modal to be used in both Events and Webhooks show view.
 */
const WebhookEventModal = ({
  webhookEventId,
  context,
  handleClose,
  handleWebhookEventUpdate = (_wehookEvent) => null,
}: WebhookEventModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [webhookEvent, setWebhookEvent] = useState<WebhookEvent | undefined>(undefined);
  const { loading } = useApiLoader({
    loader: async () => {
      const { data: webhookEvent } = await SessionClientDashboard.getResource<WebhookEvent>(
        ApiEndpoint.WebhookEvent,
        webhookEventId,
      );
      setWebhookEvent(webhookEvent);
      // The WebhookEvent could have been updated right when this modal was opened.
      handleWebhookEventUpdate(webhookEvent);
    },
    deps: [webhookEventId],
    onNetworkError: () => {
      handleClose();
      dispatch(showMessage('Failed to load event.'));
    },
    onError: () => {
      handleClose();
      dispatch(showMessage('Failed to load event.'));
    },
  });

  const { callback: handleRerunWebhookEvent, error: rerunWebhookEventError } =
    useApiCallback(async () => {
      const { data: updatedWebhookEvent } = await SessionClientDashboard.postResourceAction<
        WebhookEvent,
        ApiResource<void, void, ApiModel.WebhookEvent>
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      >({ id: webhookEvent.id, type: webhookEvent.type }, 'rerun');
      setWebhookEvent(updatedWebhookEvent);
      handleWebhookEventUpdate(updatedWebhookEvent);
    }, [webhookEvent]);
  useApiErrorHandler({
    error: rerunWebhookEventError,
    onNetworkError: () => {
      dispatch(showMessage('Failed to rerun webhook event, please try again later.'));
    },
    onError: () => {
      dispatch(showMessage('Failed to rerun webhook event, please try again later.'));
    },
  });

  if (webhookEvent == null) {
    return loading ? (
      <StyledModalContainer data-test='webhook-events-modal'>
        <ModalHeader data-test='webhook-events-modal__header' onClose={handleClose}>
          <ModalHeaderChildren>{webhookEventId}</ModalHeaderChildren>
        </ModalHeader>
        <StyledModalBody>
          <LoadingIndicator />
        </StyledModalBody>
      </StyledModalContainer>
    ) : null;
  }

  const { 'event-name': eventName, 'webhook-url': webhookUrl } = webhookEvent.attributes;

  return (
    <StyledModalContainer data-test='webhook-events-modal'>
      <ModalHeader data-test='webhook-events-modal__header' onClose={handleClose}>
        <ModalHeaderChildren>{webhookEvent.id}</ModalHeaderChildren>
      </ModalHeader>
      <StyledModalBody>
        <p>
          Event Name: <StyledMonoSpan>{eventName}</StyledMonoSpan>
        </p>
        <p>
          Webhook URL:{' '}
          <a
            href={webhookUrl}
            target='_blank'
            rel='noreferrer'
            data-test='webhook-events-modal__webhook-url'
          >
            {webhookUrl}
          </a>
        </p>
        <p data-test='webhook-events-modal__status'>
          <WebhookEventStatusSticker webhookEvent={webhookEvent} />
        </p>
        <CodeContainer>
          <Pii isRedacted={false}>
            <CodeHighlight id='webhook-events-modal__code' language='json' rowsToShow={20}>
              {JSON.stringify(webhookEvent.attributes.body, null, TAB_WIDTH)}
            </CodeHighlight>
          </Pii>
        </CodeContainer>
        <HStack equal={true}>
          {context === 'event' && (
            <Button
              width='auto'
              variant='basic'
              id='webhook-events-modal__view-webhook'
              data-test='webhook-events-modal__view-webhook'
              onClick={() =>
                navigate(`/dashboard/webhooks/${webhookEvent.relationships.webhook.data.id}`)
              }
            >
              View webhook
            </Button>
          )}
          {context === 'webhook' && (
            <Button
              width='auto'
              variant='basic'
              id='webhook-events-modal__view-event'
              data-test='webhook-events-modal__view-event'
              onClick={() =>
                navigate(`/dashboard/events/${webhookEvent.relationships.event.data.id}`)
              }
            >
              View event
            </Button>
          )}
          <Permissioned permission={Permission.WebhookEventWrite}>
            <Button
              width='auto'
              variant='primary'
              id='webhook-events-modal__button--rerun'
              data-test='webhook-events-modal__button--rerun'
              onClick={handleRerunWebhookEvent}
            >
              Rerun
            </Button>
          </Permissioned>
        </HStack>
      </StyledModalBody>
    </StyledModalContainer>
  );
};

export default WebhookEventModal;
